import { cn } from '../cn';
import Button from '../Input2/Button';
import type { SoftphoneStatus } from './softphoneClient.types';

export interface CallOutcomeOption {
  value: string;
  label: string;
}

export interface SoftphoneProps {
  contactName?: string;
  phoneNumber: string;
  status: SoftphoneStatus;
  durationSec: number;
  isMuted: boolean;
  error: string | null;
  /** When true, show a recording-consent indicator while the call is live. */
  isRecording?: boolean;
  outcomes: ReadonlyArray<CallOutcomeOption>;
  onToggleMute: () => void;
  onHangup: () => void;
  onSelectOutcome: (value: string) => void;
  onClose: () => void;
  className?: string;
}

const STATUS_LABEL: Record<SoftphoneStatus, string> = {
  idle: 'Ready',
  connecting: 'Connecting…',
  ringing: 'Ringing…',
  active: 'Connected',
  ended: 'Call ended',
  error: 'Call failed',
};

const STATUS_DOT: Record<SoftphoneStatus, string> = {
  idle: 'bg-gray-400',
  connecting: 'bg-yellow-400 animate-pulse',
  ringing: 'bg-yellow-400 animate-pulse',
  active: 'bg-green-500',
  ended: 'bg-gray-400',
  error: 'bg-red-500',
};

function formatDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function Softphone({
  contactName,
  phoneNumber,
  status,
  durationSec,
  isMuted,
  error,
  isRecording,
  outcomes,
  onToggleMute,
  onHangup,
  onSelectOutcome,
  onClose,
  className,
}: SoftphoneProps) {
  const inCall = status === 'connecting' || status === 'ringing' || status === 'active';
  const finished = status === 'ended' || status === 'error';

  return (
    <div className={cn('flex w-72 flex-col gap-4 rounded-xl bg-white p-5 dark:bg-gray-900', className)}>
      <div className="flex flex-col gap-0.5 text-center">
        <span className="truncate text-base font-semibold text-gray-900 dark:text-gray-100">
          {contactName ?? phoneNumber}
        </span>
        {contactName && <span className="truncate text-sm text-gray-500 dark:text-gray-400">{phoneNumber}</span>}
      </div>

      <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <span className={cn('h-2 w-2 rounded-full transition-colors', STATUS_DOT[status])} />
        <span>{STATUS_LABEL[status]}</span>
        {status === 'active' && <span className="tabular-nums text-gray-400">{formatDuration(durationSec)}</span>}
      </div>

      {inCall && isRecording && (
        <div className="flex items-center justify-center gap-1.5 text-xs text-red-500" data-test="recording-indicator">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
          <span>Recording</span>
        </div>
      )}

      {error && <p className="text-center text-sm text-red-500">{error}</p>}

      {inCall && (
        <div className="flex items-center justify-center gap-3">
          <Button
            tone={isMuted ? 'filled' : 'outline'}
            color={isMuted ? 'yellow' : 'gray'}
            disabled={status !== 'active'}
            onClick={onToggleMute}
          >
            {isMuted ? 'Unmute' : 'Mute'}
          </Button>
          <Button color="red" onClick={onHangup}>
            Hang up
          </Button>
        </div>
      )}

      {finished && (
        <div className="flex flex-col gap-2">
          <span className="text-center text-sm text-gray-500 dark:text-gray-400">How did it go?</span>
          <div className="grid grid-cols-2 gap-2">
            {outcomes.map((outcome) => (
              <Button key={outcome.value} size="sm" tone="outline" onClick={() => onSelectOutcome(outcome.value)}>
                {outcome.label}
              </Button>
            ))}
          </div>
          <Button size="sm" text onClick={onClose}>
            Skip
          </Button>
        </div>
      )}
    </div>
  );
}
